import { Col, Row } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Div, Dropdown, FormItem, FormText, SubTitle } from 'ui';
// eslint-disable-next-line
import ReferenceDataDropdown from 'common/reference-data/containers/reference-data-dropdown-container';
import { referenceTypes } from '../../../utils/app-configs';

export const Divider = styled.div`
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const Organisation = ({
  data: {
    salesOfficeName,
    salesOfficeNumber,
    plantName,
    lastVisitTechnicianName,
    plannerGroupName,
    plannerGroupNumber,
    salesGroupName,
    salesGroupNumber,
    plant,
    mainWorkCenterName,
    mainWorkCenterNumber,
    recommendedEngineers = [],
  },
  isEditable,
  form: {
    setFieldsValue,
    getFieldValue,
    getFieldDecorator,
  },
  disabled,
  intl,
}) => {
  const [selectedSalesOffice, setSelectedSalesOffice] = useState('idle');

  const onSalesOfficeChanged = (v) => {
    setFieldsValue({
      salesOfficeName: v?.name,
    });
    setSelectedSalesOffice(v);
  };

  // Needs to be useEffect
  useEffect(() => {
    // if the sales office changes, check if the sales group is part of the sales office.
    const salesGroup = getFieldValue('salesGroupNumber');
    if (
      selectedSalesOffice !== 'idle' &&
      (
        !selectedSalesOffice ||
        !selectedSalesOffice.items ||
        !selectedSalesOffice.items?.find(g => g.number === salesGroup)
      )
    ) {
      setFieldsValue({
        salesGroupNumber: '',
        salesGroupName: '',
      });
    }
  }, [selectedSalesOffice]);

  const onFieldChanged = (field, v) => {
    setFieldsValue({
      [field]: v?.name || v?.label,
    });
  };

  // Sales office decorators
  const salesOfficeValidator = getFieldDecorator('salesOfficeNumber', {
    initialValue: salesOfficeNumber,
  });
  getFieldDecorator('salesOfficeName', {
    initialValue: salesOfficeName,
  });

  // Sales group decorators
  const salesGroupValidator = getFieldDecorator('salesGroupNumber', {
    initialValue: salesGroupNumber,
  });
  getFieldDecorator('salesGroupName', {
    initialValue: salesGroupName,
  });

  // Planner group decorators
  const plannerGroupValidator = getFieldDecorator('plannerGroupNumber', {
    initialValue: plannerGroupNumber,
  });
  getFieldDecorator('plannerGroupName', {
    initialValue: plannerGroupName,
  });

  const mainWorkcenterValidator = getFieldDecorator('mainWorkCenterNumber', {
    initialValue: mainWorkCenterNumber,
  });
  getFieldDecorator('mainWorkCenterName', {
    initialValue: mainWorkCenterName,
  });

  return (
    <Div marginBottom={20} data-test-id='organisation'>
      <SubTitle>{intl.formatMessage({ id: 'common.titles.ORGANISATION' })}</SubTitle>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.organisation.SALES_GROUP' })}>
            {isEditable ?
              salesGroupValidator(
                <Dropdown
                  placeholder={intl.formatMessage({
                    id: 'equipment.organisation.SELECT_SALES_GROUP',
                  })}
                  onChange={v => onFieldChanged('salesGroupName', v)}
                  values={selectedSalesOffice ? selectedSalesOffice.items : []}
                  disabled={!selectedSalesOffice || disabled}
                  valueLabel='name'
                  valueKey='number'
                />,
              )
              :
              <FormText>{salesGroupName || salesGroupNumber}</FormText>
            }
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.organisation.SALES_OFFICE' })}>
            {isEditable ?
              salesOfficeValidator(
                <ReferenceDataDropdown
                  placeholder={intl.formatMessage({
                    id: 'equipment.organisation.SELECT_SALES_OFFICE',
                  })}
                  onObjectChanged={onSalesOfficeChanged}
                  disabled={disabled}
                  type={referenceTypes.equipment}
                  name='salesOffices'
                  valueLabel='name'
                  valueKey='number'
                />,
              )
              :
              <FormText>{salesOfficeName || salesOfficeNumber}</FormText>
            }
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.organisation.PLANT' })}>
            <FormText>{plantName}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.organisation.PLANNER_GROUP' })}>
            {isEditable ?
              plannerGroupValidator(
                <ReferenceDataDropdown
                  placeholder={intl.formatMessage({
                    id: 'equipment.organisation.SELECT_PLANNER_GROUP',
                  })}
                  onObjectChanged={v => onFieldChanged('plannerGroupName', v)}
                  disabled={disabled}
                  type={referenceTypes.equipment}
                  name='plannerGroups'
                  valueLabel='name'
                  valueKey='number'
                  urlParams={{ plant }}
                />,
              )
              :
              <FormText>{plannerGroupName || plannerGroupNumber}</FormText>
            }
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24} data-test-id='organisation.preferred-tech'>
          <FormItem
            label={intl.formatMessage({ id: 'equipment.organisation.PREFERRED_TECHNICIAN' })}
          >
            {isEditable ?
              mainWorkcenterValidator(
                <ReferenceDataDropdown
                  placeholder={intl.formatMessage({
                    id: 'equipment.general.SELECT_WORKCENTER',
                  })}
                  onObjectChanged={v => onFieldChanged('mainWorkCenterName', v)}
                  disabled={disabled}
                  type={referenceTypes.equipment}
                  name='workCenters'
                  valueLabel='name'
                  valueKey='workCenterNumber'
                />,
              )
              :
              <FormText>{mainWorkCenterName}</FormText>
            }
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem
            label={intl.formatMessage({ id: 'equipment.organisation.LAST_VISIT_TECHNICIAN' })}
          >
            <FormText>{lastVisitTechnicianName}</FormText>
          </FormItem>
        </Col>
      </Row>
      {recommendedEngineers?.length ?
        <Row type='flex' gutter={16}>
          <Col md={24} sm={24} xs={24}>
            <FormItem
              label={intl.formatMessage({ id: 'equipment.organisation.RECOMMENDED_TECHNICIAN' })}
            >
              <Row type='flex' gutter={16}>
                {recommendedEngineers
                  .sort((a, b) => a.priority - b.priority)
                  .map(re =>
                    <Col key={re.number} md={8} sm={24} xs={24}>
                      <FormText>{re.name}</FormText>
                    </Col>,
                  )}
              </Row>
            </FormItem>
          </Col>
        </Row > :
        null
      }
    </Div >
  );
};

Organisation.propTypes = {
  data: PropTypes.object,
  isEditable: PropTypes.bool,
  form: PropTypes.object,
  disabled: PropTypes.bool,
  intl: PropTypes.object,
};

export default injectIntl(Organisation);
